import React, { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Input, Modal, Radio, Space, Spin, Typography } from 'antd'
import { postCase } from '../../../../api/arendeApi'
import { useApi } from '../../../../api/useApi'
import { ACTIONS, CaseKind } from './reasons'

const { Text } = Typography
const { TextArea } = Input

export interface HandleFlagRow {
  key: React.Key
  flagga: number
  flaggnamn: string
  namn: string
  nivå: number
}

interface Props {
  open: boolean
  rows: HandleFlagRow[]
  onClose: () => void
  onDone: () => void
}

/**
 * "Hantera flagga" (#30 PR 4).
 *
 * Två handlingar, inga fler. Tilldelning finns medvetet INTE med: den kräver en definierad
 * process för vem som gör vad, och den processen ska tas fram tillsammans med kommunerna.
 * Att bygga den nu vore att gissa och sedan låsa fast gissningen i ett gränssnitt.
 *
 * Syftet i det här skedet är att kunna sortera och filtrera på läge, samt att samla
 * statistik om varför flaggor avfärdas — det senare är den enda återkopplingen
 * flaggbygget får om att en tröskel eller målgrupp är fel.
 *
 * Ett steg, inte två: med bara två val vore en kort-sida följd av en fält-sida en klick
 * för mycket i något som ska gå snabbt.
 */
const HandleFlagModal = ({ open, rows, onClose, onDone }: Props) => {
  const api = useApi()
  const [kind, setKind] = useState<CaseKind>('hanterad')
  const [reasons, setReasons] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Nollställ vid varje öppning — annars ligger föregående val kvar och kan sparas av
  // misstag på en helt annan flagga.
  useEffect(() => {
    if (open) {
      setKind('hanterad')
      setReasons([])
      setComment('')
      setError(null)
    }
  }, [open])

  const action = ACTIONS.find((a) => a.kind === kind)
  const antal = rows.length

  const save = async () => {
    setSaving(true)
    setError(null)

    const res = await postCase(api, {
      id_token: localStorage.getItem('hydra_id_token') ?? '',
      kind,
      sub_reasons: reasons,
      comment,
      flags: rows.map((r) => ({ flagId: r.key, flagType: r.flagga })),
    })

    setSaving(false)

    // postCase sväljer fel och returnerar felobjektet — utan den här kontrollen ser en
    // misslyckad sparning ut som en lyckad.
    if (res instanceof Error || !res?.id) {
      setError('Kunde inte spara. Försök igen.')
      return
    }

    onDone()
  }

  return (
    <Modal
      open={open}
      title={antal > 1 ? `Hantera flaggor (${antal})` : 'Hantera flagga'}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="avbryt" onClick={onClose} disabled={saving}>
          Avbryt
        </Button>,
        // Fast bredd: knapptexten byts när man växlar handling, och utan den hoppar
        // knappen i storlek mitt under valet. Båda etiketterna är 20 tecken -> ~174 px
        // inklusive padding. Antalet står i modalens rubrik och upprepas inte här.
        //
        // Ingen `loading` här — snurran ligger över hela innehållet (Spin nedan), som i
        // resten av appen. En snurra inuti knappen byter dessutom ut etiketten mitt i.
        <Button key="spara" type="primary" onClick={save} disabled={saving || reasons.length === 0} style={{ width: 180 }}>
          {action?.title}
        </Button>,
      ]}
    >
      <Spin spinning={saving}>
      {antal > 1 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Val sätts på samtliga flaggor."
        />
      )}

      <Radio.Group value={kind} onChange={(e) => { setKind(e.target.value); setReasons([]) }} style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          {ACTIONS.map((a) => (
            <Radio key={a.kind} value={a.kind}>
              {a.title}
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, lineHeight: '18px' }}>{a.description}</div>
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      <div style={{ marginTop: 20 }}>
        <Text strong>Skäl</Text>
        <Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
          (markera minst ett skäl)
        </Text>

        <Checkbox.Group value={reasons} onChange={(v) => setReasons(v as string[])} style={{ width: '100%', marginTop: 8 }}>
          <Space direction="vertical">
            {action?.reasons.map((r) => (
              <Checkbox key={r.key} value={r.key}>
                {r.label}
              </Checkbox>
            ))}
          </Space>
        </Checkbox.Group>
      </div>

      <div style={{ marginTop: 20 }}>
        <Text strong>Kommentar</Text>
        <Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
          (valfritt)
        </Text>
        <TextArea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} style={{ marginTop: 8 }} maxLength={500} />
      </div>

        {error && <Alert type="error" showIcon style={{ marginTop: 16 }} message={error} />}
      </Spin>
    </Modal>
  )
}

export default HandleFlagModal
